import { useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import {
  CK, FONT_BODY, FONT_MONO, statusChipSx, tableHeadRowSx, headCellSx, bodyCellSx,
  emptyTextSx, actionRowSx,
} from '../portalStyles';

export const JIT_ROLE_HELPER_TEXT =
  "Accounts you don't select get the role automatically the first time a StackSet deploys to them.";

const accountName = (a) => a.name || a.accountName || '';

// ExecutionRoleAccountPicker is the searchable account table used at enable
// (setup wizard) and by "Add accounts" on the roles page. Nothing is ticked
// unless the parent passes a selection; Select all ticks every account the
// current search shows, Clear unticks everything.
export default function ExecutionRoleAccountPicker({
  accounts, selected, onChange, adminAccountId, maxHeight = 360,
}) {
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => a.accountId.includes(q) || accountName(a).toLowerCase().includes(q));
  }, [accounts, search]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };
  const selectAll = () => {
    const next = new Set(selected);
    visible.forEach((a) => next.add(a.accountId));
    onChange(next);
  };
  const clear = () => onChange(new Set());

  const visibleSelected = visible.filter((a) => selected.has(a.accountId)).length;
  const allVisibleSelected = visible.length > 0 && visibleSelected === visible.length;

  return (
    <Box>
      <Box sx={{ ...actionRowSx, mb: 1.5 }}>
        <TextField
          size="small"
          label="Search accounts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 0, flex: '1 1 220px', maxWidth: 340 }}
        />
        <Button size="small" variant="outlined" onClick={selectAll} disabled={visible.length === 0 || allVisibleSelected}>
          Select all
        </Button>
        <Button size="small" onClick={clear} disabled={selected.size === 0}>
          Clear
        </Button>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.muted, ml: 'auto' }}>
          {selected.size} of {accounts.length} selected
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight, border: `1px solid ${CK.border}`, borderRadius: '8px' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={tableHeadRowSx}>
              <TableCell padding="checkbox" sx={{ ...headCellSx, backgroundColor: CK.pageBg }} />
              <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Account</TableCell>
              <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Account ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((a) => (
              <TableRow
                key={a.accountId}
                hover
                onClick={() => toggle(a.accountId)}
                sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 'none' } }}
              >
                <TableCell padding="checkbox" sx={bodyCellSx}>
                  <Checkbox
                    size="small"
                    checked={selected.has(a.accountId)}
                    // The row toggles on click; the box handles its own change
                    // and stops the click so it does not toggle twice.
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(a.accountId)}
                    inputProps={{ 'aria-label': `Select ${accountName(a) || a.accountId}` }}
                  />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  {accountName(a) || '—'}
                  {a.accountId === adminAccountId && (
                    <Chip label="admin" size="small" sx={{ ...statusChipSx('purple'), ml: 1 }} />
                  )}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, fontFamily: FONT_MONO }}>{a.accountId}</TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3, borderBottom: 'none' }}>
                  <Typography sx={emptyTextSx}>
                    {accounts.length === 0 ? 'No accounts to choose from.' : 'No accounts match the search.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
